import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FlagIcon from '@mui/icons-material/Flag';
import { useTranslation } from 'react-i18next';
import useHoverNavigation from '../use-hover-navigation';
import { hoverHighlightSx } from '../hover-style';
import { useConnect4Socket } from './connect4-provider';
import { ROWS, COLS, PLAYER_COLORS, findLowestEmptyRow } from './connect4-utils';
import GameOverDialog from './game-over-dialog';

const CELL_SIZE = 64;
const GAP = 4;

const barSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'box-shadow 0.3s ease, background-color 0.3s ease',
    borderRadius: 4,
    gap: 1,
};

export default function GameBoard({ gameState, onBack }) {
    const { t } = useTranslation();
    const socket = useConnect4Socket();

    const [board, setBoard] = useState(gameState.board);
    const [currentTurn, setCurrentTurn] = useState(gameState.currentTurn);
    const [gameOver, setGameOver] = useState(null);
    const [winningCells, setWinningCells] = useState(null);

    const yourIndex = gameState.yourIndex;
    const isMyTurn = currentTurn === yourIndex;
    const players = gameState.players;

    // 7 columns + forfeit + back = 9 items when it's your turn, 0 otherwise
    const itemCount = gameOver ? 0 : (isMyTurn ? COLS + 2 : 0);

    const onSelect = useCallback((index) => {
        if (index < COLS) {
            if (socket && findLowestEmptyRow(board, index) !== -1) {
                socket.emit('game:move', { roomId: gameState.roomId, column: index });
            }
        } else if (index === COLS) {
            if (socket) socket.emit('game:forfeit', { roomId: gameState.roomId });
        } else {
            if (socket) socket.emit('game:forfeit', { roomId: gameState.roomId });
            onBack();
        }
    }, [socket, board, gameState.roomId, onBack]);

    const { active, activeIndex, paused, pause } = useHoverNavigation(itemCount, onSelect);

    // Listen for game updates
    useEffect(() => {
        if (!socket) return;

        const handleUpdate = ({ board: newBoard, currentTurn: newTurn }) => {
            setBoard(newBoard);
            setCurrentTurn(newTurn);
        };

        const handleOver = ({ winner, winningCells: cells, reason }) => {
            setGameOver({ winner, reason });
            setWinningCells(cells);
            pause();
        };

        socket.on('game:update', handleUpdate);
        socket.on('game:over', handleOver);

        return () => {
            socket.off('game:update', handleUpdate);
            socket.off('game:over', handleOver);
        };
    }, [socket, pause]);

    // When Enter is pressed while paused (dialog showing), resume dismisses → go back
    useEffect(() => {
        if (gameOver && !paused) {
            onBack();
        }
    }, [gameOver, paused, onBack]);

    const isWinningCell = (r, c) => {
        if (!winningCells) return false;
        return winningCells.some(([wr, wc]) => wr === r && wc === c);
    };

    const activeColumn = active && activeIndex < COLS ? activeIndex : null;
    const ghostRow = activeColumn !== null ? findLowestEmptyRow(board, activeColumn) : -1;

    const statusText = gameOver
        ? ''
        : isMyTurn
            ? t('APPLICATIONS.CONNECT4.YOUR_TURN')
            : t('APPLICATIONS.CONNECT4.OPPONENT_TURN');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            {/* Status bar */}
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {statusText}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <Box component="span" sx={{ color: PLAYER_COLORS[yourIndex], fontWeight: 700 }}>●</Box>
                    {' '}{players[yourIndex]}
                    {' — '}
                    <Box component="span" sx={{ color: PLAYER_COLORS[1 - yourIndex], fontWeight: 700 }}>●</Box>
                    {' '}{players[1 - yourIndex]}
                </Typography>
            </Box>

            {/* Board — main area */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                <Box sx={{ display: 'inline-flex', flexDirection: 'column', bgcolor: '#1565c0', borderRadius: 3, p: 1 }}>
                    {/* Column headers (hover targets) */}
                    <Box sx={{ display: 'flex', gap: `${GAP}px` }}>
                        {Array.from({ length: COLS }).map((_, c) => (
                            <Box
                                key={c}
                                sx={{
                                    width: CELL_SIZE,
                                    height: 16,
                                    borderRadius: 1,
                                    transition: 'all 0.2s ease',
                                    ...(active && activeColumn === c && {
                                        bgcolor: PLAYER_COLORS[yourIndex],
                                        ...hoverHighlightSx,
                                        outlineOffset: 2,
                                    }),
                                }}
                            />
                        ))}
                    </Box>

                    {/* Grid */}
                    {Array.from({ length: ROWS }).map((_, r) => (
                        <Box key={r} sx={{ display: 'flex', gap: `${GAP}px`, mt: `${GAP}px` }}>
                            {Array.from({ length: COLS }).map((_, c) => {
                                const cell = board[r][c];
                                const isGhost = ghostRow === r && activeColumn === c && cell === null;
                                const isWin = isWinningCell(r, c);

                                return (
                                    <Box
                                        key={c}
                                        sx={{
                                            width: CELL_SIZE,
                                            height: CELL_SIZE,
                                            borderRadius: '50%',
                                            bgcolor: cell !== null
                                                ? PLAYER_COLORS[cell]
                                                : isGhost
                                                    ? PLAYER_COLORS[yourIndex]
                                                    : '#e3f2fd',
                                            opacity: isGhost ? 0.4 : 1,
                                            transition: 'background-color 0.2s ease, opacity 0.2s ease',
                                            ...(isWin && {
                                                boxShadow: '0 0 12px 4px rgba(255,255,255,0.8)',
                                                outline: '3px solid white',
                                            }),
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Bottom bar — forfeit + back */}
            <Box sx={{ flex: '0 0 15%', display: 'flex', gap: 1, mx: 2, mb: 2 }}>
                <Box
                    onClick={() => onSelect(COLS)}
                    sx={{
                        ...barSx,
                        flex: 1,
                        bgcolor: 'error.light',
                        color: 'error.contrastText',
                        py: 1,
                        ...(active && activeIndex === COLS && hoverHighlightSx),
                    }}
                >
                    <FlagIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('APPLICATIONS.CONNECT4.FORFEIT')}
                    </Typography>
                </Box>
                <Box
                    onClick={() => onSelect(COLS + 1)}
                    sx={{
                        ...barSx,
                        flex: 1,
                        bgcolor: 'grey.300',
                        color: 'text.primary',
                        py: 1,
                        ...(active && activeIndex === COLS + 1 && hoverHighlightSx),
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('APPLICATIONS.BACK')}
                    </Typography>
                </Box>
            </Box>

            <GameOverDialog
                open={gameOver !== null}
                winner={gameOver?.winner}
                yourIndex={yourIndex}
                reason={gameOver?.reason}
            />
        </Box>
    );
}
